import axios, { type AxiosResponse } from "axios";
import API from "../../../rest/api";
import { encryptDataByCrypto } from "../../../components/functions/utilities";
import { CONNECTION_HUB_ENDPOINTS } from "../constants/connectionHub.constants";
import type {
  AccountRow,
  AgencyContextInput,
  ApiError,
  ApiResponse,
  ChannelCatalogResponse,
  ConnectionInput,
  ConnectionScopeInput,
  DelegateInboxResponse,
  DelegationView,
  DraftView,
  EmailSuggestions,
  InboxGroup,
  RemoveConnectionResult,
  RemoveDraftResult,
  ResolveResult,
  SaveDraftInput,
  SaveSubBrandsInput,
  SetupInput,
  SetupResponse,
  StandaloneCard,
  UpdateCredentialsInput,
} from "./connectionHub.types";
import { handleConnectionError, type HandleConnectionErrorOptions } from "../utils/handleConnectionError";

const agencyParams = (agencyClientId?: number | null): Record<string, number> =>
  typeof agencyClientId === "number" ? { agencyClientId } : {};

const agencyBody = (agencyClientId?: number | null): Record<string, number> =>
  typeof agencyClientId === "number" ? { agencyClientId } : {};

// The backend rejects an empty list, so "whole inbox" is sent as no field at all.
const scopeBody = (connectionIds?: string[]): { connectionIds?: string[] } =>
  connectionIds?.length ? { connectionIds } : {};

const unwrap = <T>(response: AxiosResponse<ApiResponse<T>>): T => {
  if (response.data.success === true) return response.data.data;
  throw response.data as ApiError;
};

const execute = async <T>(
  request: () => Promise<AxiosResponse<ApiResponse<T>>>,
  options?: HandleConnectionErrorOptions
): Promise<T> => {
  try {
    return unwrap(await request());
  } catch (error) {
    throw handleConnectionError(error, options);
  }
};

const publicAPI = axios.create({
  baseURL: `${import.meta.env.VITE_JIGSAW_DEV_REST_LOCATION}`,
  responseType: "json",
  headers: { "ngrok-skip-browser-warning": "69420" },
});

const executePublic = async <T>(request: () => Promise<AxiosResponse<ApiResponse<T>>>): Promise<T> => {
  try {
    return unwrap(await request());
  } catch (error) {
    throw handleConnectionError(error);
  }
};

const encryptPassword = <T extends { password?: string }>(input: T): T => {
  if (!input.password) return { ...input };
  return {
    ...input,
    password: encryptDataByCrypto(input.password) as string,
  };
};

const encryptChannels = (channels: ConnectionInput[]): ConnectionInput[] =>
  channels.map(encryptPassword);

export const connectionHubApi = {
  async getChannels(agencyClientId?: number | null): Promise<ChannelCatalogResponse> {
    return execute(() =>
      API.get<ApiResponse<ChannelCatalogResponse>>(
        CONNECTION_HUB_ENDPOINTS.channels,
        { params: agencyParams(agencyClientId) }
      )
    );
  },

  async getAccounts(
    ipId: string,
    agencyClientId?: number | null
  ): Promise<AccountRow[]> {
    return execute(() =>
      API.get<ApiResponse<AccountRow[]>>(
        CONNECTION_HUB_ENDPOINTS.accounts(ipId),
        { params: agencyParams(agencyClientId) }
      )
    );
  },

  async getEmailSuggestions(
    agencyClientId?: number | null
  ): Promise<EmailSuggestions> {
    return execute(() =>
      API.get<ApiResponse<EmailSuggestions>>(
        CONNECTION_HUB_ENDPOINTS.emailSuggestions,
        { params: agencyParams(agencyClientId) }
      )
    );
  },

  async saveDrafts(input: SaveDraftInput): Promise<{ drafts: DraftView[] }> {
    const body = {
      channels: encryptChannels(input.channels),
      ...agencyBody(input.agencyClientId),
    };
    return execute(() =>
      API.put<ApiResponse<{ drafts: DraftView[] }>>(
        CONNECTION_HUB_ENDPOINTS.drafts,
        body
      )
    );
  },

  async removeDraft(
    ipId: string,
    agencyClientId?: number | null
  ): Promise<RemoveDraftResult> {
    return execute(() =>
      API.delete<ApiResponse<RemoveDraftResult>>(
        CONNECTION_HUB_ENDPOINTS.draft(ipId),
        { params: agencyParams(agencyClientId) }
      )
    );
  },

  async setup(input: SetupInput): Promise<SetupResponse> {
    const body = {
      channels: encryptChannels(input.channels),
      ...agencyBody(input.agencyClientId),
    };
    return execute(() =>
      API.post<ApiResponse<SetupResponse>>(
        CONNECTION_HUB_ENDPOINTS.setup,
        body
      )
    );
  },

  async resolveSetup(
    ipIds: string[],
    agencyClientId?: number | null,
    connectionId?: string
  ): Promise<ResolveResult> {
    return execute(() =>
      API.get<ApiResponse<ResolveResult>>(
        CONNECTION_HUB_ENDPOINTS.resolveSetup,
        { params: { ipIds, ...(connectionId ? { connectionId } : {}), ...agencyParams(agencyClientId) } }
      )
    );
  },

  async confirmForwarding(
    inboxId: string,
    input: ConnectionScopeInput = {}
  ): Promise<InboxGroup> {
    return execute(() =>
      API.post<ApiResponse<InboxGroup>>(
        CONNECTION_HUB_ENDPOINTS.forwardingConfirmation(inboxId),
        { ...scopeBody(input.connectionIds), ...agencyBody(input.agencyClientId) }
      )
    );
  },

  async confirmFilter(
    inboxId: string,
    input: ConnectionScopeInput = {}
  ): Promise<InboxGroup> {
    return execute(() =>
      API.post<ApiResponse<InboxGroup>>(
        CONNECTION_HUB_ENDPOINTS.filterConfirmation(inboxId),
        { ...scopeBody(input.connectionIds), ...agencyBody(input.agencyClientId) }
      )
    );
  },

  async recheckInbox(
    inboxId: string,
    input: AgencyContextInput = {}
  ): Promise<InboxGroup> {
    return execute(() =>
      API.post<ApiResponse<InboxGroup>>(
        CONNECTION_HUB_ENDPOINTS.inboxRecheck(inboxId),
        agencyBody(input.agencyClientId)
      )
    );
  },

  async verifyInbox(
    inboxId: string,
    input: ConnectionScopeInput = {}
  ): Promise<InboxGroup> {
    return execute(() =>
      API.post<ApiResponse<InboxGroup>>(
        CONNECTION_HUB_ENDPOINTS.inboxVerification(inboxId),
        { ...scopeBody(input.connectionIds), ...agencyBody(input.agencyClientId) }
      )
    );
  },

  async getDelegation(
    inboxId: string,
    agencyClientId?: number | null
  ): Promise<DelegationView> {
    return execute(() =>
      API.get<ApiResponse<DelegationView>>(
        CONNECTION_HUB_ENDPOINTS.delegation(inboxId),
        { params: agencyParams(agencyClientId) }
      )
    );
  },

  async resendDelegation(
    inboxId: string,
    input: AgencyContextInput = {}
  ): Promise<DelegationView> {
    return execute(() =>
      API.post<ApiResponse<DelegationView>>(
        CONNECTION_HUB_ENDPOINTS.delegationResend(inboxId),
        agencyBody(input.agencyClientId)
      )
    );
  },

  async verifyConnection(
    connectionId: string,
    input: AgencyContextInput = {}
  ): Promise<InboxGroup | StandaloneCard> {
    return execute(() =>
      API.post<ApiResponse<InboxGroup | StandaloneCard>>(
        CONNECTION_HUB_ENDPOINTS.connectionVerification(connectionId),
        agencyBody(input.agencyClientId)
      )
    );
  },

  async saveSubBrands(
    connectionId: string,
    input: SaveSubBrandsInput
  ): Promise<InboxGroup | StandaloneCard> {
    return execute(() =>
      API.post<ApiResponse<InboxGroup | StandaloneCard>>(
        CONNECTION_HUB_ENDPOINTS.connectionSubBrands(connectionId),
        { subBrands: input.subBrands, ...agencyBody(input.agencyClientId) }
      )
    );
  },

  async updateCredentials(
    connectionId: string,
    input: UpdateCredentialsInput
  ): Promise<InboxGroup | StandaloneCard> {
    const { agencyClientId, ...credentials } = input;
    return execute(() =>
      API.patch<ApiResponse<InboxGroup | StandaloneCard>>(
        CONNECTION_HUB_ENDPOINTS.connectionCredentials(connectionId),
        { ...encryptPassword(credentials), ...agencyBody(agencyClientId) }
      )
    );
  },

  async removeConnection(
    connectionId: string,
    agencyClientId?: number | null
  ): Promise<RemoveConnectionResult> {
    return execute(() =>
      API.delete<ApiResponse<RemoveConnectionResult>>(
        CONNECTION_HUB_ENDPOINTS.connection(connectionId),
        { params: agencyParams(agencyClientId) }
      )
    );
  },

  async getDelegateInbox(token: string): Promise<DelegateInboxResponse> {
    return executePublic(() =>
      publicAPI.get<ApiResponse<DelegateInboxResponse>>(CONNECTION_HUB_ENDPOINTS.delegate(token))
    );
  },

  async updateDelegateCredentials(token: string, connectionId: string, input: { password: string }): Promise<InboxGroup> {
    return executePublic(() =>
      publicAPI.patch<ApiResponse<InboxGroup>>(
        CONNECTION_HUB_ENDPOINTS.delegateCredentials(token, connectionId),
        encryptPassword(input)
      )
    );
  },

  async confirmDelegateForwarding(token: string, connectionIds?: string[]): Promise<InboxGroup> {
    return executePublic(() =>
      publicAPI.post<ApiResponse<InboxGroup>>(
        CONNECTION_HUB_ENDPOINTS.delegateForwardingConfirmation(token),
        scopeBody(connectionIds)
      )
    );
  },

  async confirmDelegateFilter(token: string, connectionIds?: string[]): Promise<InboxGroup> {
    return executePublic(() =>
      publicAPI.post<ApiResponse<InboxGroup>>(
        CONNECTION_HUB_ENDPOINTS.delegateFilterConfirmation(token),
        scopeBody(connectionIds)
      )
    );
  },

  async verifyDelegateInbox(token: string, connectionIds?: string[]): Promise<InboxGroup> {
    return executePublic(() =>
      publicAPI.post<ApiResponse<InboxGroup>>(
        CONNECTION_HUB_ENDPOINTS.delegateVerification(token),
        scopeBody(connectionIds)
      )
    );
  },
};
